/*
 * ECO 活动业务状态。
 *
 * 复购信息同时被资产总览和复购页使用：在此做短时内存缓存与并发请求合并，
 * 避免切换页面时重复请求动态的资格、库存和报价数据。
 * 社区长信息用于控制推荐页的社区长专区入口。
 */
const REPURCHASE_INFO_CACHE_DURATION = 10 * 1000
const COMMUNITY_LEADER_INFO_CACHE_DURATION = 10 * 1000
let repurchaseInfoFetchPromise = null
let communityLeaderInfoFetchPromise = null

const useEcoStore = defineStore('ecoStore', {
  state: () => ({
    repurchaseInfo: null,
    repurchaseInfoFetchedAt: 0,
    repurchaseInfoLoading: false,
    repurchaseInfoRequestVersion: 0,
    communityLeaderInfo: null,
    communityLeaderInfoFetchedAt: 0,
    communityLeaderInfoLoading: false,
    communityLeaderInfoRequestVersion: 0,
  }),
  actions: {
    async fetchRepurchaseInfo ({ force = false } = {}) {
      const now = Date.now()
      const isCacheValid = this.repurchaseInfo && now - this.repurchaseInfoFetchedAt < REPURCHASE_INFO_CACHE_DURATION

      if (!force && isCacheValid) {
        return this.repurchaseInfo
      }

      if (repurchaseInfoFetchPromise) {
        return repurchaseInfoFetchPromise
      }

      const requestVersion = this.repurchaseInfoRequestVersion
      this.repurchaseInfoLoading = true

      const request = (async () => {
        const { data, execute } = ecoApi.getRepurchaseInfo({}, { immediate: false })
        await execute()

        const info = data.value?.data
        if (!info || typeof info !== 'object') {
          return null
        }

        // 登出或切换账号后，不允许未完成的旧请求回写新会话的数据。
        if (this.repurchaseInfoRequestVersion !== requestVersion) {
          return null
        }

        this.repurchaseInfo = info
        this.repurchaseInfoFetchedAt = Date.now()
        return info
      })()

      repurchaseInfoFetchPromise = request

      try {
        return await request
      } finally {
        if (repurchaseInfoFetchPromise === request) {
          repurchaseInfoFetchPromise = null
        }
        if (this.repurchaseInfoRequestVersion === requestVersion) {
          this.repurchaseInfoLoading = false
        }
      }
    },
    clearRepurchaseInfo () {
      this.repurchaseInfoRequestVersion += 1
      this.repurchaseInfo = null
      this.repurchaseInfoFetchedAt = 0
      this.repurchaseInfoLoading = false
      repurchaseInfoFetchPromise = null
    },
    async fetchCommunityLeaderInfo ({ force = false } = {}) {
      const now = Date.now()
      const isCacheValid = this.communityLeaderInfo && now - this.communityLeaderInfoFetchedAt < COMMUNITY_LEADER_INFO_CACHE_DURATION

      if (!force && isCacheValid) {
        return this.communityLeaderInfo
      }

      if (communityLeaderInfoFetchPromise) {
        return communityLeaderInfoFetchPromise
      }

      const requestVersion = this.communityLeaderInfoRequestVersion
      this.communityLeaderInfoLoading = true

      const request = (async () => {
        const { data, execute } = ecoApi.getCommunityLeaderInfo({}, { immediate: false })
        await execute()

        const info = data.value?.data
        if (!info || typeof info !== 'object') {
          return null
        }

        if (this.communityLeaderInfoRequestVersion !== requestVersion) {
          return null
        }

        this.communityLeaderInfo = info
        this.communityLeaderInfoFetchedAt = Date.now()
        return info
      })()

      communityLeaderInfoFetchPromise = request

      try {
        return await request
      } finally {
        if (communityLeaderInfoFetchPromise === request) {
          communityLeaderInfoFetchPromise = null
        }
        if (this.communityLeaderInfoRequestVersion === requestVersion) {
          this.communityLeaderInfoLoading = false
        }
      }
    },
    clearCommunityLeaderInfo () {
      this.communityLeaderInfoRequestVersion += 1
      this.communityLeaderInfo = null
      this.communityLeaderInfoFetchedAt = 0
      this.communityLeaderInfoLoading = false
      communityLeaderInfoFetchPromise = null
    },
  },
})

export default useEcoStore
